import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2, MessageSquare, Send, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";

const QUESTIONS = [
  { key: "search_usefulness",        label: "Was search useful in finding works you would consider?" },
  { key: "metadata_usefulness",      label: "Was the metadata (mood, genre, BPM, best use) useful?" },
  { key: "clearance_status_clarity", label: "Was the clearance-readiness status clear?" },
  { key: "missing_information",      label: "What information was missing that you would normally want?" },
  { key: "ease_of_use",              label: "How was the ease of use?" },
  { key: "helpful_for_real_workflow", label: "Would this help your real sync / music-supervision workflow?" },
  { key: "open_comments",            label: "Anything else? Open comments are welcome." },
];

export default function BetaFeedback() {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const initialTrack = search.get("track") || "";

  const [trackId, setTrackId] = useState(initialTrack);
  const [trackPreview, setTrackPreview] = useState(null);
  const [form, setForm] = useState(() =>
    Object.fromEntries(QUESTIONS.map((q) => [q.key, ""])),
  );
  const [rating, setRating] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (!trackId) {
      setTrackPreview(null);
      return;
    }
    let cancelled = false;
    api.get(`/beta/catalog/${trackId}`)
      .then((r) => { if (!cancelled) setTrackPreview(r.data); })
      .catch(() => { if (!cancelled) setTrackPreview({ _error: true }); });
    return () => { cancelled = true; };
  }, [trackId]);

  function setQ(k, v) { setForm((s) => ({ ...s, [k]: v })); }

  const hasContent = useMemo(() => {
    return rating > 0 || Object.values(form).some((v) => (v || "").trim());
  }, [form, rating]);

  async function submit(e) {
    e?.preventDefault?.();
    setSubmitError("");
    if (!hasContent) {
      setSubmitError("Please share at least one comment or a star rating before submitting.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        track_id: trackId || null,
        ...form,
        rating: rating || null,
      };
      await api.post("/beta/feedback", payload);
      toast.success("Thank you — your feedback has been recorded.");
      navigate("/beta/my-feedback");
    } catch (e) {
      setSubmitError(errMsg(e, "Could not submit feedback."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="beta-feedback-page">
      <Button asChild variant="ghost" size="sm" className="-ml-3" data-testid="beta-feedback-back">
        <Link to={trackId ? `/beta/track/${trackId}` : "/beta"}><ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back</Link>
      </Button>
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">Industry Beta · Feedback</div>
        <h1 className="font-display text-3xl tracking-tight flex items-center gap-3">
          <MessageSquare className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
          Share your perspective
        </h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Your feedback goes directly to the Kandake team. We share it internally for product
          improvement; we never share your responses with other reviewers, clients, or artists.
        </p>
      </div>

      {trackId && trackPreview && !trackPreview._error && (
        <Card data-testid="beta-feedback-track-preview">
          <CardHeader>
            <CardTitle className="text-base">Feedback context</CardTitle>
            <CardDescription>You are sharing feedback about a specific work:</CardDescription>
          </CardHeader>
          <CardContent className="text-[13.5px]">
            <div className="font-medium" data-testid="beta-feedback-track-title">{trackPreview.track_title}</div>
            <div className="text-muted-foreground">{trackPreview.artist || "\u2014"} · {trackPreview.genre || "\u2014"}</div>
          </CardContent>
        </Card>
      )}
      {trackId && trackPreview?._error && (
        <Alert variant="destructive">
          <AlertDescription>
            That work is not currently in the beta catalog. You can still submit general feedback below;
            we will record it without a work attached.
          </AlertDescription>
        </Alert>
      )}
      {!trackId && (
        <Alert>
          <AlertDescription>
            You are submitting general beta feedback (not attached to a specific work). To attach a work,
            open it from the catalog and use the \u201cGive feedback\u201d action.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={submit} className="space-y-5" data-testid="beta-feedback-form">
        {QUESTIONS.map((q) => (
          <div key={q.key}>
            <Label className="text-[12.5px]">{q.label}</Label>
            <Textarea
              rows={3}
              value={form[q.key]}
              onChange={(e) => setQ(q.key, e.target.value)}
              data-testid={`beta-fb-${q.key}`}
            />
          </div>
        ))}
        <div>
          <Label className="text-[12.5px]">Optional rating</Label>
          <div className="flex items-center gap-1 mt-1" role="radiogroup" aria-label="Optional rating" data-testid="beta-fb-rating">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                role="radio"
                aria-checked={rating === n}
                onClick={() => setRating(rating === n ? 0 : n)}
                className="p-1.5 hover:opacity-80"
                data-testid={`beta-fb-rating-${n}`}
              >
                <Star
                  className={`w-6 h-6 ${n <= rating ? "fill-amber-400 stroke-amber-500" : "stroke-slate-400"}`}
                />
              </button>
            ))}
            {rating > 0 && (
              <button
                type="button"
                className="text-[11.5px] underline text-muted-foreground ml-2"
                onClick={() => setRating(0)}
                data-testid="beta-fb-rating-clear"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {submitError && (
          <Alert variant="destructive" data-testid="beta-fb-error">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={busy} data-testid="beta-fb-submit">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
            <Send className="w-3.5 h-3.5 mr-1.5" /> Submit feedback
          </Button>
        </div>
      </form>
    </div>
  );
}
